import {
  DAYS_WITHOUT_REMINDER,
  PriorityStatus,
  determinePriorityStatus,
} from '@client/util/priorityStatus';
import { Action, createSelector } from '@reduxjs/toolkit';
import {
  AdvisorSponsorshipStatus,
  SessionType,
  assertNotNull,
  emptyObject,
  fullName,
  initials,
  isFamily,
  isNullUndefinedOrEmpty,
  mapDefined,
  nonLatinLettersRegex,
  standardizeSearchText,
} from '@shared';
import { StripeSession } from '@shared/types/StripeSession';
import { AppState } from './store';

export function selectActiveApiActions(state: AppState) {
  return state.activeApiActions;
}

export const selectIsApiActionActive = createSelector(
  selectActiveApiActions,
  (_state, apiActionToCheck: Action<string>) => apiActionToCheck,
  (activeApiActions, apiActionToCheck) =>
    (activeApiActions as unknown[]).includes(apiActionToCheck),
);

export function selectAdvisorState(state: AppState) {
  return state.advisor;
}

export function selectAdvisorUpdatesStatus(state: AppState) {
  return state.advisor.advisorUpdates;
}

export function selectClients(state: AppState) {
  return state.clients.data;
}

export function selectAdvisorHasClients(state: AppState) {
  return state.clients.data.length > 0;
}

export function selectClientsIsLoading(state: AppState) {
  return state.clients.isLoading;
}

export function selectClientIsInvalidating(state: AppState) {
  return state.clients.isInvalidating;
}

export function selectClientDuplicationStatus(state: AppState) {
  return state.clients.clientDuplicationStatus;
}

export function selectClientDeletionStatus(state: AppState) {
  return state.client.deletionStatus;
}

export function selectClientsSearchText(state: AppState) {
  return state.clientsSearchText;
}

export const selectClientsWithSearchableNames = createSelector(selectClients, clients =>
  clients?.map(client => ({
    client,
    searchableName: standardizeSearchText(client.clientFirstName, client.clientLastName),
  })),
);

export const selectClientsSearchResults = createSelector(
  selectClients,
  selectClientsWithSearchableNames,
  selectClientsSearchText,
  (clients, clientsWithSearchableNames, clientsSearchText) => {
    if (isNullUndefinedOrEmpty(clientsSearchText)) {
      return clients;
    }
    const searchTerms = clientsSearchText
      .toLocaleLowerCase()
      .split(nonLatinLettersRegex)
      .filter(term => term !== 'and' && term !== 'or' && term !== 'not');

    return mapDefined(clientsWithSearchableNames, ({ client, searchableName }) => {
      return searchTerms.some(term => searchableName.includes(term)) ? client : undefined;
    });
  },
);

export function selectMaybeClient(state: AppState) {
  return state.client.data ?? null;
}

export function selectClientDataExists(state: AppState) {
  return state.client.data !== null;
}

export function selectMaybeClientPartner(state: AppState) {
  return state.client.clientPartner ?? null;
}

export const selectClient = createSelector(
  [
    (state: AppState) => state.client.data,
    (state: AppState) => state.client.phaseCosts,
    (state: AppState) => state.client.supportProviderSet,
  ],
  (clientData, phaseCosts, supportProviderSet) => {
    const validClientData = assertNotNull(clientData, 'state.client.data');
    const validPhaseCosts = assertNotNull(phaseCosts, 'state.client.phaseCosts');

    return {
      ...validClientData,
      phaseCosts: validPhaseCosts.length > 0 ? validPhaseCosts : validClientData.phaseCosts,
      supportProviderSet: supportProviderSet || validClientData.supportProviderSet,
    };
  },
);

export function selectClientSupportProviderSet(state: AppState) {
  return state.client.supportProviderSet;
}

export function selectClientIsLoading(state: AppState) {
  return state.client.isLoading;
}

export function selectClientIsPartialLoading(state: AppState) {
  return state.client.isPartialLoading;
}

export function selectClientStatus(state: AppState) {
  return state.client.status;
}

export function selectClientUpdatesStatus(state: AppState) {
  return state.client.clientUpdates;
}

export function selectClientErrorMessage(state: AppState) {
  return state.client.errorMessage;
}

export function selectPartialClient(state: AppState) {
  return (state.client.data || emptyObject) as Partial<Client>;
}

function createClientSelector<T>(
  fieldSelector: (client: Client) => T,
): ReturnType<typeof createSelector<[(state: AppState) => Client], T>> {
  return createSelector(selectClient, client => fieldSelector(client));
}

export const selectSurveys = createClientSelector(({ surveys }) => surveys);

export const selectSurveysAreLoaded = createSelector(
  selectMaybeClient,
  maybeClient => maybeClient !== null, // there is always an intake survey, so once loaded the array will never be blank
);

export const selectIntakeSurvey = createClientSelector(client => client.intakeSurvey);

export const selectInferenceSet = createClientSelector(({ inferenceSet }) => inferenceSet);

export const selectAppliedInferenceSet = createClientSelector(
  ({ appliedInferenceSet }) => appliedInferenceSet,
);

export const selectSupportProviderSet = createClientSelector(
  ({ supportProviderSet }) => supportProviderSet,
);

export const selectFamilySupportProviders = createSelector(
  selectSupportProviderSet,
  supportProviderSet => supportProviderSet.supportProviders.filter(isFamily),
);

export const selectFundingSources = createClientSelector(({ fundingSources }) => fundingSources);

export const selectSelfFundingSource = createSelector(
  selectFundingSources,
  ({ selfFunding }) => selfFunding,
);

export const selectSelfFundingContributionYears = createSelector(
  selectInferenceSet,
  selectSelfFundingSource,
  ({ ltcAtYear }, { contributionStartYear, contributionEndYear }) => [
    contributionStartYear ?? new Date().getFullYear(),
    contributionEndYear ?? ltcAtYear,
  ],
);

export const selectLtcPolicyFundingSource = createSelector(
  selectFundingSources,
  ({ ltcPolicy }) => ltcPolicy,
);

export function selectLtcFinancialPlanningIsLoading(state: AppState) {
  return state.ltcFinancialPlanning.isLoading;
}

export const selectLtcFinancialPlanning = createSelector(
  [(state: AppState) => state.ltcFinancialPlanning],
  ltcFinancialPlanning => ltcFinancialPlanning,
);

export const selectLtcFinancialPlanningAccountDetails = createSelector(
  [selectClient],
  (client): Partial<Record<string, LtcFinancialPlanningAccountDetail>> => {
    const selfFunding = client.multipleFundingSources?.selfFunding || {};
    const accountDetails: Partial<Record<string, LtcFinancialPlanningAccountDetail>> = {};

    // Build account details from all self-funding sources
    Object.entries(selfFunding).forEach(([fundingSourceId, source]) => {
      const accountType = source.accountType;
      if (!accountType) return;

      // Determine if the source is complete (all required fields are non-null)
      const isComplete =
        source.existingAssetsValue !== null &&
        source.annualRateOfReturn !== null &&
        source.gainsTaxRate !== null;

      accountDetails[accountType] = {
        currentValue: source.existingAssetsValue || 0,
        futureContributions: source.monthlyContributionAmount || 0,
        expectedReturn: source.annualRateOfReturn || 0,
        capitalGainsTaxRate: source.gainsTaxRate || 0,
        ltcFundingPortion: 0,
        isComplete,
        fundingSourceId,
      };
    });

    return accountDetails;
  },
);

export const selectPolicyPreferences = createSelector(
  selectLtcFinancialPlanning,
  ltcFinancialPlanning => ltcFinancialPlanning.policyPreferences,
);

export const selectIsQuestionnaireComplete = createSelector(
  selectPolicyPreferences,
  preferences =>
    preferences.deathBenefit !== null &&
    preferences.paymentStructure !== null &&
    preferences.benefitStructure !== null,
);

export const selectLtcFinancialPlanningSelectedPolicyTypes = createSelector(
  selectLtcFinancialPlanning,
  ltcFinancialPlanning => ltcFinancialPlanning.selectedPolicyTypes,
);

export const selectLtcFinancialPlanningSelectedAccountTypes = createSelector(
  selectLtcFinancialPlanning,
  ltcFinancialPlanning => ltcFinancialPlanning.selectedAccountTypes,
);

export function selectSession(state: AppState) {
  return state.session;
}

export const selectClientProfiles = createSelector(selectSession, session => {
  if (session.sessionType === SessionType.client) {
    return assertNotNull(session.clientProfiles, 'state.session.clientProfiles');
  }
  return null;
});

export const selectClientLocalCareEnvironmentCosts = createSelector(
  selectClient,
  client => client.clientLocalCareEnvironmentCosts,
);

export const selectSessionAdvisor = createSelector(
  [selectSession, selectMaybeClient, selectClientProfiles],
  (session, clientData, clientProfiles) => {
    if (session.sessionType === SessionType.client && clientData !== null) {
      const clientProfile = clientProfiles?.find(
        profile => profile.clientId === clientData.clientId,
      );
      return assertNotNull(clientProfile?.clientAdvisor, 'state.session.advisor');
    }
    return assertNotNull(session.advisor, 'state.session.advisor');
  },
);

export const selectAdvisorOrganizations = createSelector(
  selectSessionAdvisor,
  advisor => advisor.advisorOrganizations || [],
);

export const selectShouldDisallowUsage = createSelector(selectSession, session => {
  // Wizards and Genworth advisors get full access without an active seat.
  if (session.sessionType === SessionType.wizard || session.advisor?.featureFlags?.genworthMode) {
    return false;
  }

  return session.seatStatus !== 'active';
});

export const selectSponsorshipAccessStatus = createSelector(
  selectSession,
  ({ stripe }: { stripe: StripeSession | null }) => {
    let isPartOfSponsorship = false;
    let advisorSponsorshipStatus = null;

    if (
      stripe?.advisorSponsorshipStatus &&
      [AdvisorSponsorshipStatus.active, AdvisorSponsorshipStatus.invited].includes(
        stripe?.advisorSponsorshipStatus,
      )
    ) {
      isPartOfSponsorship = true;
      advisorSponsorshipStatus = stripe.advisorSponsorshipStatus;
    }

    return {
      advisorSponsorshipStatus,
      isPartOfSponsorship,
    };
  },
);

export const selectStripeSubscribeSessionState = (state: AppState) => state.stripeSubscribeSession;

export const selectSponsorshipState = (state: AppState) => state.sponsorship;

export const selectSponsorshipId = (state: AppState) => state.session.stripe.sponsorshipId;

export const selectSessionFullName = createSelector(selectSession, session =>
  session === null ? '' : fullName(session),
);

export const selectSessionInitials = createSelector(selectSession, session =>
  session === null ? '' : initials(session),
);

export function selectSessionIsLoading(state: AppState) {
  return state.session.isLoading;
}

export const selectClientOnboardingSlideProgress = createSelector(
  selectClient,
  client => client.onboardingSlideProgress,
);

export function selectEmailPreferences(state: AppState) {
  return state.emailPreferences;
}

export function selectClientEmailState(state: AppState) {
  return state.clientEmail;
}

export function selectClientEmailErrorMessageState(state: AppState) {
  return state.clientEmail.errorMessage;
}

export function selectFeatureFlags(state: AppState) {
  // For advisor sessions, use the advisor's feature flags directly
  if (state.session.sessionType !== SessionType.client) {
    return state.session.advisor?.featureFlags ?? {};
  }

  // For client sessions, use the advisor's feature flags from client profiles
  const { clientProfiles } = state.session;

  if (!Array.isArray(clientProfiles) || clientProfiles.length === 0) {
    return {};
  }

  const currentClientAdvisorId = state.client.data?.advisorId;
  let clientAdvisor = null;

  if (currentClientAdvisorId) {
    // Find the advisor matching the current client's advisor ID
    const matchingProfile = clientProfiles.find(
      profile => profile.advisorId === currentClientAdvisorId,
    );
    clientAdvisor = matchingProfile?.clientAdvisor ?? null;
  } else {
    // Fall back to the first profile's advisor
    clientAdvisor = clientProfiles[0]?.clientAdvisor ?? null;
  }

  return clientAdvisor?.featureFlags ?? {};
}

export function selectPolicyExtractor(state: AppState) {
  return state.policyExtractor;
}

export function selectDataTable(state: AppState) {
  return state.dataTable;
}

export function selectEmailResendInviteState(state: AppState) {
  return state.resendTemporaryPasswordEmail;
}

export function selectVerifyPartnerLinkRequestState(state: AppState) {
  return state.verifyPartnerLinkRequest;
}

export function selectAdvisorHierarchyState(state: AppState) {
  return state.advisorHierarchy;
}

// Advisor Hierarchy Invitation Mutation Selectors
export const selectHierarchyInvitationMutationStatus = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => ({
    status: hierarchyState.invitationMutationStatus,
    error: hierarchyState.invitationMutationError,
    isMutating: hierarchyState.invitationMutationStatus === 'loading',
  }),
);

export const selectAdvisorHierarchyInvitationAcceptResponse = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => hierarchyState.invitationAcceptResponse,
);

export const selectAdvisorHierarchyInvitationDeclineResponse = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => hierarchyState.invitationDeclineResponse,
);

export const selectSupervisorAdvisorRelationships = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => hierarchyState.supervisorAdvisorRelationships,
);

export const selectPendingSupervisorInvitations = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => hierarchyState.pendingSupervisorInvitations,
);

export const selectHierarchyDirectRelationshipMutationStatus = createSelector(
  [selectAdvisorHierarchyState],
  hierarchyState => ({
    isMutating: hierarchyState.directRelationshipMutationStatus === 'loading',
    status: hierarchyState.directRelationshipMutationStatus,
    error: hierarchyState.directRelationshipMutationError,
  }),
);

export function selectClientCalculationSettings(state: AppState) {
  return state.client.data?.clientCalculationSettings ?? null;
}

export function selectAdvisorAdminPortalState(state: AppState) {
  return state.advisorAdminPortal;
}

export function selectAdvisorOpportunitiesState(state: AppState) {
  return state.advisorOpportunities;
}

export const selectIsLoadingQualifyingClients = createSelector(
  [selectAdvisorOpportunitiesState],
  (advisorOpportunities): boolean => {
    return Object.values(advisorOpportunities).some(state => state.status === 'fetchingClients');
  },
);

export const selectQualifyingOpportunityClients = createSelector(
  [selectClients, selectAdvisorOpportunitiesState],
  (allClients, advisorOpportunities): Record<ClientEmailEngagement, Client[]> => {
    const engagementTypes = Object.keys(advisorOpportunities) as ClientEmailEngagement[];

    return engagementTypes.reduce(
      (acc, engagementType) => {
        const qualifyingClientIds = new Set(
          advisorOpportunities[engagementType].qualifyingClients.map(
            (client: Client) => client.clientId,
          ),
        );

        return {
          ...acc,
          [engagementType]: allClients.filter(client => qualifyingClientIds.has(client.clientId)),
        };
      },
      {} as Record<ClientEmailEngagement, Client[]>,
    );
  },
);

export const selectEligibleClientsForEngagement = createSelector(
  [selectAdvisorOpportunitiesState, selectQualifyingOpportunityClients],
  (
    advisorOpportunities,
    qualifyingOpportunityClients,
  ): Record<
    ClientEmailEngagement,
    { reminderEligible: Client[]; reminderIneligible: Client[] }
  > => {
    const clientAddedDateTimeEligibilityCheck = (client: Client, xDaysAgo: Date) => {
      return client.clientAddedDateTime < xDaysAgo;
    };
    const engagementTypes = Object.keys(advisorOpportunities) as ClientEmailEngagement[];
    return engagementTypes.reduce(
      (acc, engagementType) => ({
        ...acc,
        [engagementType]: sortFilterReminderEligibleClients(
          qualifyingOpportunityClients[engagementType],
          engagementType,
          engagementType === 'REVIEW_RESULTS_TOGETHER'
            ? undefined
            : { customEligibilityCheck: clientAddedDateTimeEligibilityCheck },
        ),
      }),
      {} as Record<
        ClientEmailEngagement,
        { reminderEligible: Client[]; reminderIneligible: Client[] }
      >,
    );
  },
);

function sortFilterReminderEligibleClients(
  qualifyingClients: Client[] | undefined,
  engagementType: ClientEmailEngagement,
  options?: {
    customEligibilityCheck?: (client: Client, xDaysAgo: Date) => boolean;
  },
): { reminderEligible: Client[]; reminderIneligible: Client[] } {
  if (!qualifyingClients) {
    return { reminderEligible: [], reminderIneligible: [] };
  }

  const xDaysAgo = new Date();
  xDaysAgo.setDate(xDaysAgo.getDate() - DAYS_WITHOUT_REMINDER);

  // Filter out clients without email first
  const clientsWithEmail = qualifyingClients.filter(client => client.clientEmail);

  // Separate clients into eligible and ineligible
  const { reminderEligible, reminderIneligible } = clientsWithEmail.reduce<{
    reminderEligible: Client[];
    reminderIneligible: Client[];
  }>(
    (acc, client) => {
      const lastReminder = client.latestEmailReminders?.[engagementType];
      const baseEligibility = !lastReminder || new Date(lastReminder) < xDaysAgo;
      const isEligible = options?.customEligibilityCheck
        ? baseEligibility && options.customEligibilityCheck(client, xDaysAgo)
        : baseEligibility;

      if (isEligible) {
        acc.reminderEligible.push(client);
      } else {
        acc.reminderIneligible.push(client);
      }

      return acc;
    },
    { reminderEligible: [], reminderIneligible: [] },
  );

  // Sort function for both eligible and ineligible clients
  const sortByReminderDate = (a: Client, b: Client) => {
    const dateA = a.latestEmailReminders?.[engagementType];
    const dateB = b.latestEmailReminders?.[engagementType];

    // If either client doesn't have a reminder date, put them first
    if (!dateA) return -1;
    if (!dateB) return 1;

    // Sort by oldest first
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  };

  // Sort both arrays
  const sortedReminderEligible = reminderEligible.sort(sortByReminderDate);
  const sortedReminderIneligible = reminderIneligible.sort(sortByReminderDate);

  return {
    reminderEligible: sortedReminderEligible,
    reminderIneligible: sortedReminderIneligible,
  };
}

export const selectSortedQualifyingOpportunityClients = createSelector(
  [selectEligibleClientsForEngagement],
  (eligibleClientsForEngagement): Record<ClientEmailEngagement, Client[]> => {
    const engagementTypes = Object.keys(eligibleClientsForEngagement) as ClientEmailEngagement[];

    return engagementTypes.reduce(
      (acc, engagementType) => ({
        ...acc,
        [engagementType]: [
          ...eligibleClientsForEngagement[engagementType].reminderEligible,
          ...eligibleClientsForEngagement[engagementType].reminderIneligible,
        ],
      }),
      {} as Record<ClientEmailEngagement, Client[]>,
    );
  },
);

export const selectPriorityStatusByEngagement = createSelector(
  [selectQualifyingOpportunityClients, selectEligibleClientsForEngagement],
  (
    qualifyingClients,
    clientsEligibleForReminder,
  ): Record<ClientEmailEngagement, PriorityStatus> => {
    const engagementTypes = Object.keys(qualifyingClients) as ClientEmailEngagement[];

    return engagementTypes.reduce(
      (acc, engagementType) => ({
        ...acc,
        [engagementType]: determinePriorityStatus(
          qualifyingClients[engagementType],
          clientsEligibleForReminder[engagementType].reminderEligible,
          'Highly recommended',
        ),
      }),
      {} as Record<ClientEmailEngagement, PriorityStatus>,
    );
  },
);

export const selectOpportunityActionRequired = createSelector(
  [selectPriorityStatusByEngagement, selectClients],
  (priorityStatusByEngagement, clients) => {
    // Due to the addition of the invite client CTA opportunity
    // which is different from the other opportunities, we're appending
    // this check. We want to indicate that the user should invite new
    // clients only when they don't already have any.
    if (clients.length === 0) return true;

    return Object.values(priorityStatusByEngagement).some(
      status => status !== 'Not applicable' && status !== 'No action required',
    );
  },
);

export const selectOauth = createSelector(selectSession, session => session.oauth);
export const selectQuotes = (state: AppState) => state.quotes.data;
export const selectHasFivePayQuotes = (state: AppState) => state.quotes.hasFivePayQuotes;
export const selectAllPaymentOptionQuotes = (state: AppState) =>
  state.quotes.allPaymentOptionQuotes;
export const selectIsLoadingAllPaymentOptions = (state: AppState) =>
  state.quotes.isLoadingAllPaymentOptions;
export const selectQuotesIsLoading = (state: AppState) => state.quotes.isLoading;
export const selectQuotesError = (state: AppState) => state.quotes.error;
export const selectTraditionalLTCQuotes = (state: AppState) => state.quotes.traditionalLTCQuotes;
export const selectTraditionalLTCQuotesIsLoading = (state: AppState) =>
  state.quotes.isLoadingTraditionalLTC;
export const selectTraditionalLTCQuotesError = (state: AppState) =>
  state.quotes.traditionalLTCError;

// LTC Planning Quotes (separate from shop insurance quotes)
export const selectLtcPlanningQuotes = (state: AppState) => state.ltcPlanningQuotes?.data ?? [];
export const selectLtcPlanningQuotesIsLoading = (state: AppState) =>
  state.ltcPlanningQuotes?.isLoading ?? false;
export const selectLtcPlanningQuotesError = (state: AppState) =>
  state.ltcPlanningQuotes?.error ?? null;

export const selectPreLogin = createSelector(selectSession, session => session.preLogin);

export const selectPolicyApplicationModel = (state: AppState) => state.policyApplicationModel;

export const selectPolicyApplications = (state: AppState) =>
  state.policyApplicationModel.applications;

export const selectPreviousFilledApplicationForm = createSelector(
  [
    (state: AppState) => selectPolicyApplications(state),
    (_state: AppState, clientId: string) => clientId,
    (_state: AppState, _clientId: string, stepType: string) => stepType,
    (_state: AppState, _clientId: string, _stepType: string, formId: string) => formId,
    (
      _state: AppState,
      _clientId: string,
      _stepType: string,
      _formId: string,
      currentClientPolicyApplicationId: string,
    ) => currentClientPolicyApplicationId,
  ],
  (applications, clientId, stepType, formId, currentClientPolicyApplicationId) => {
    return applications
      .filter(
        app =>
          app.clientId === clientId &&
          app.clientPolicyApplicationId !== currentClientPolicyApplicationId,
      )
      .flatMap(app =>
        app.steps
          .filter(
            step =>
              ['in_progress', 'completed'].includes(step.status) &&
              step.clientPolicyApplicationForm?.submissionData,
          )
          .map(step => ({
            clientPolicyApplicationId: app.clientPolicyApplicationId,
            policyApplicationFormId: step.policyApplicationFormId,
            stepType: step.stepType,
            status: step.status,
            submissionData: step.clientPolicyApplicationForm?.submissionData || {},
            visitedSubsections: step.clientPolicyApplicationForm?.visitedSubsections,
            submissionDateTime: step.clientPolicyApplicationForm?.submissionDateTime,
            stateOfResidenceAbbreviation: app.stateOfResidenceAbbreviation,
            fundingPolicyType: app.fundingPolicyType,
          })),
      );
  },
);

export const selectPartnerPolicyApplications = (state: AppState) =>
  state.partnerPolicyApplicationModel.applications;

export const selectPolicyApplicationsStatus = (state: AppState) =>
  state.policyApplicationModel.status;

export const selectPolicyApplicationsError = (state: AppState) =>
  state.policyApplicationModel.error;

export const selectLastCreatedApplicationId = (state: AppState) =>
  state.policyApplicationModel.lastCreatedApplicationId;

export const selectFormIdCache = (state: AppState) => state.policyApplicationModel.formIdCache;

export const selectCachedFormId = createSelector(
  [selectFormIdCache, (_state: AppState, cacheKey: string) => cacheKey],
  (formIdCache, cacheKey) => formIdCache[cacheKey] || undefined,
);

export const selectFormConfigCache = (state: AppState) =>
  state.policyApplicationModel.formConfigCache;

export const selectCachedFormConfig = createSelector(
  [selectFormConfigCache, (_state: AppState, formId: string) => formId],
  (formConfigCache, formId) => (formId in formConfigCache ? formConfigCache[formId] : undefined),
);
export const selectIllustration = (state: AppState) => state.illustration;

export const selectIllustrationResponseDataByQuoteId = createSelector(
  [selectIllustration, (_state: AppState, insuranceQuoteId: string) => insuranceQuoteId],
  (illustration, insuranceQuoteId) =>
    illustration.completedIllustrations?.[insuranceQuoteId]?.responseData || null,
);

export const selectHasShownErrorToast = createSelector(
  selectIllustration,
  (_state: AppState, errorKey: string) => errorKey,
  (illustration: IllustrationState, errorKey: string) =>
    Boolean(illustration.shownErrorToasts?.[errorKey]),
);
export const selectHasActiveIllustrationRequest = (state: AppState) =>
  state.illustration.hasActiveRequest;

export const selectOpenAiCompletion = (state: AppState) => state.openAiCompletion;

export const selectOpenAiCompletionByRequestId = createSelector(
  [selectOpenAiCompletion, (_state: AppState, requestId: string) => requestId],
  (openAiCompletion, requestId) => openAiCompletion.completions[requestId],
);

export const selectOpenAiCompletionSearch = (state: AppState) => state.openAiCompletionSearch;

export const selectTypeformPayload = (state: AppState) => state.typeformPayload.data;

export const selectTypeformPayloadIsLoading = (state: AppState) => state.typeformPayload.isLoading;

export const selectTypeformPayloadError = (state: AppState) => state.typeformPayload.error;

const selectPrefillDataState = (state: AppState) => state.policyApplicationPrefill;

// Get all prefill entries
const selectAllPrefillEntries = createSelector(
  selectPrefillDataState,
  prefillState => prefillState.entries,
);

// Get specific prefill data by clientId, forClientId, and formId
export const selectPrefillDataForClient = createSelector(
  [
    selectAllPrefillEntries,
    (_state: AppState, clientId: string) => clientId,
    (_state: AppState, _clientId: string, clientPolicyApplicationId: string) =>
      clientPolicyApplicationId,
    (_state: AppState, _clientId: string, _clientPolicyApplicationId: string, formId: string) =>
      formId,
  ],
  (entries, clientId, clientPolicyApplicationId, formId) => {
    const entry = entries.find(
      e =>
        e.clientId === clientId &&
        e.clientPolicyApplicationId === clientPolicyApplicationId &&
        e.formId === formId,
    );
    return entry;
  },
);

export const selectAiUnderwritingTypeform = (state: AppState) => state.aiUnderwritingTypeform;

export const selectAdvisorBgaSettings = (state: AppState) => state.advisorBgaCarrierSettings.bgas;

export const selectAdvisorCarriers = (state: AppState) => state.advisorBgaCarrierSettings.carriers;
// Consent selectors
export const selectConsent = (state: AppState) => state.consent;

export const selectApplicationConsentStatus = (state: AppState) =>
  state.consent.applicationConsentStatus;

export const selectPostConsentRecord = (state: AppState) => state.consent.postConsentRecord;

export const selectApplicationConsentStatusData = createSelector(
  [selectApplicationConsentStatus],
  applicationConsentStatus => applicationConsentStatus.data,
);

export const selectApplicationConsentStatusIsLoading = createSelector(
  [selectApplicationConsentStatus],
  applicationConsentStatus => applicationConsentStatus.isLoading,
);

export const selectPostConsentRecordData = createSelector(
  [selectPostConsentRecord],
  postConsentRecord => postConsentRecord.data,
);

export const selectPostConsentRecordIsLoading = createSelector(
  [selectPostConsentRecord],
  postConsentRecord => postConsentRecord.isLoading,
);

export const selectPostConsentRecordErrorMessage = createSelector(
  [selectPostConsentRecord],
  postConsentRecord => postConsentRecord.errorMessage,
);

export const selectSendConsentNotificationEmail = (state: AppState) =>
  state.consent.sendConsentNotificationEmail;

export const selectSendConsentNotificationEmailData = createSelector(
  [selectSendConsentNotificationEmail],
  sendConsentNotificationEmail => sendConsentNotificationEmail.data,
);

export const selectSendConsentNotificationEmailIsLoading = createSelector(
  [selectSendConsentNotificationEmail],
  sendConsentNotificationEmail => sendConsentNotificationEmail.isLoading,
);

export const selectSendConsentNotificationEmailError = createSelector(
  [selectSendConsentNotificationEmail],
  sendConsentNotificationEmail => sendConsentNotificationEmail.errorMessage,
);

export const selectPostRecordCarePacketDownload = (state: AppState) =>
  state.consent.postRecordCarePacketDownload;

export const selectPostRecordCarePacketDownloadData = createSelector(
  [selectPostRecordCarePacketDownload],
  postRecordCarePacketDownload => postRecordCarePacketDownload.data,
);

export const selectPostRecordCarePacketDownloadIsLoading = createSelector(
  [selectPostRecordCarePacketDownload],
  postRecordCarePacketDownload => postRecordCarePacketDownload.isLoading,
);

export const selectPostRecordCarePacketDownloadErrorMessage = createSelector(
  [selectPostRecordCarePacketDownload],
  postRecordCarePacketDownload => postRecordCarePacketDownload.errorMessage,
);

// Advisor Onboarding selectors
export const selectAdvisorOnboarding = (state: AppState) => state.advisorOnboarding;

export const selectAdvisorOnboardingData = createSelector(
  [selectAdvisorOnboarding],
  advisorOnboarding => advisorOnboarding.data,
);

export const selectAdvisorOnboardingStatus = createSelector(
  [selectAdvisorOnboarding],
  advisorOnboarding => advisorOnboarding.status,
);

export const selectAdvisorOnboardingChecklistData = createSelector(
  [selectAdvisorOnboardingData],
  data => data?.checklist ?? null,
);

export const selectAdvisorOnboardingCompletedStepsData = createSelector(
  [selectAdvisorOnboardingData],
  data => data?.completedSteps ?? null,
);

export const selectHasSkippedOnboardingStep = createSelector(
  [selectAdvisorOnboarding],
  advisorOnboarding => advisorOnboarding.hasSkippedOnboardingStep,
);

// Impersonation selectors
export const selectImpersonationTarget = (state: AppState) => state.impersonation.targetAdvisorId;
export const selectIsImpersonating = (state: AppState) =>
  state.session.impersonation?.isImpersonating ?? false;

// Subscription Seats selectors
export const selectSubscriptionSeatsState = (state: AppState) => state.subscriptionSeats;

export const selectSubscriptionSeats = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.seats,
);

export const selectSubscription = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.subscription,
);

export const selectIsSubscriptionOwner = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.isOwner,
);

// Fetch status selectors
export const selectFetchStatus = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => ({
    status: subscriptionSeatsState.fetchStatus,
    error: subscriptionSeatsState.fetchError,
    isFetching: subscriptionSeatsState.fetchStatus === 'loading',
  }),
);

// Mutation status selectors
export const selectMutationStatus = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => ({
    status: subscriptionSeatsState.mutationStatus,
    error: subscriptionSeatsState.mutationError,
    currentMutation: subscriptionSeatsState.currentMutation,
    isMutating: subscriptionSeatsState.mutationStatus === 'loading',
  }),
);

// Invitation preview selectors
export const selectInvitationPreviewStatus = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => ({
    status: subscriptionSeatsState.invitationPreviewStatus,
    error: subscriptionSeatsState.invitationPreviewError,
    isPreviewingInvitation: subscriptionSeatsState.invitationPreviewStatus === 'loading',
  }),
);

export const selectInvitationPreview = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.invitationPreview,
);

// Other data selectors
export const selectCreditBalance = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.customer?.creditBalance || 0,
);

export const selectUnitPricePerSeat = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.subscription?.unitPricePerSeat || 0,
);

export const selectSeatInvitationResponse = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.seatInvitationResponse,
);

export const selectPendingSeatInvitations = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.pendingSeatInvitations,
);

export const selectSeatInvitationAcceptResponse = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.seatInvitationAcceptResponse,
);

export const selectSeatInvitationDeclineResponse = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.seatInvitationDeclineResponse,
);

export const selectSubscriptionOwnerInfo = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.ownerInfo,
);

export const selectCurrentAdvisorAssignedDateTime = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.currentAdvisorAssignedDateTime,
);

export const selectCurrentAdvisorExpiresDateTime = createSelector(
  [selectSubscriptionSeatsState],
  subscriptionSeatsState => subscriptionSeatsState.currentAdvisorExpiresDateTime,
);

// Funding source operation tracking selectors
export const selectActiveFundingSourceOperation = (state: AppState) =>
  state.client.activeFundingSourceOperation;

export const selectFundingSourceSaveError = (state: AppState) =>
  state.client.fundingSourceSaveError;

export const selectClientLoadError = (state: AppState) => state.client.loadError;

export const makeSelectIsInsuranceQuoteLoading = (
  insuranceQuoteId: string,
  fundingSourceId?: string | null,
): ((state: AppState) => boolean) =>
  createSelector([selectActiveFundingSourceOperation], activeFundingSourceOperation => {
    if (!activeFundingSourceOperation) return false;

    // Check by insuranceQuoteId (for add/edit operations)
    if (activeFundingSourceOperation.insuranceQuoteId === insuranceQuoteId) {
      return true;
    }

    // Check by fundingSourceId (for delete operations)
    if (fundingSourceId && activeFundingSourceOperation.fundingSourceId === fundingSourceId) {
      return true;
    }

    return false;
  });
